import Layout from '@/components/Layout';
import HeroSection from '@/components/HeroSection';
import FeaturesSection from '@/components/FeaturesSection';
import FleetSection from '@/components/FleetSection';
import ABCalculator from '@/components/calculator/ABCalculator';
import PricingTable from '@/components/PricingTable';
import TestimonialsSection from '@/components/TestimonialsSection';
import ContactSection from '@/components/ContactSection';
import FAQSection from '@/components/FAQSection';
import { services } from '@/data/services';
import { useLanguage } from '@/contexts/LanguageContext';
import LocalizedLink from '@/components/LocalizedLink';
import { ArrowRight } from 'lucide-react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';

const ServicesPreview = () => {
  const { t } = useLanguage();
  const featured = services;

  return (
    <section className="py-20 md:py-28 bg-background">
      <div className="container">
        <div className="text-center mb-16">
          <h2 className="font-display text-3xl md:text-4xl font-bold text-foreground mb-4">
            {t.servicesPage?.title || 'Our Services'}
          </h2>
          <div className="w-16 h-1 bg-gradient-gold mx-auto mt-6 rounded-full" />
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
          {featured.map((s, i) => (
            <motion.div key={s.slug} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }}>
              <LocalizedLink to={`/services/${s.slug}`} className="flex items-start gap-4 p-5 bg-card rounded-2xl shadow-card border border-border hover:shadow-gold/20 hover:-translate-y-1 transition-all group">
                <div className="w-12 h-12 rounded-xl bg-gradient-gold flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                  <s.icon className="w-6 h-6 text-primary-foreground" />
                </div>
                <div>
                  <h3 className="font-display text-base font-semibold text-foreground mb-1 group-hover:text-primary transition-colors">
                    {t.services?.[s.titleKey] || s.titleKey}
                  </h3>
                  <p className="text-muted-foreground text-sm line-clamp-2">{t.services?.[s.descKey] || s.descKey}</p>
                </div>
              </LocalizedLink>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

const Index = () => {
  return (
    <Layout>
      <Helmet>
        <title>Taxi Salzburg | Airport Transfer & Taxi Service 24/7 | +43 660 6521071</title>
        <meta name="description" content="Taxi Salzburg - Ihr zuverlässiger Partner für günstige Taxi-Transfers, Flughafentransfers und private Tagestouren. Professionelle Fahrer, moderne Fahrzeuge. 24/7: +43 660 6521071" />
        <link rel="canonical" href="https://www.taxisalzburg.net" />
      </Helmet>
      <HeroSection />
      <FeaturesSection />
      <ServicesPreview />
      <FleetSection />
      <ABCalculator />
      <PricingTable />
      <TestimonialsSection />
      <FAQSection />
      <ContactSection />

      {/* JSON-LD Structured Data */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "TaxiService",
        "name": "Taxi Salzburg",
        "url": "https://www.taxisalzburg.net",
        "telephone": "+43-660-6521071",
        "email": "office@taxisalzburg.net",
        "address": { "@type": "PostalAddress", "addressLocality": "Salzburg", "addressCountry": "AT" },
        "areaServed": ["Salzburg", "Austria", "Bavaria", "Hallstatt", "Vienna", "Munich", "Innsbruck"],
        "description": "Ihr zuverlässiger Partner für günstige und exklusive Taxi-Transfers, Flughafentransfers und private Tagestouren in Salzburg und Umgebung.",
        "openingHoursSpecification": { "@type": "OpeningHoursSpecification", "dayOfWeek": ["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday","Sunday"], "opens": "00:00", "closes": "23:59" },
        "priceRange": "€€",
        "paymentAccepted": "Cash, Credit Card",
        "sameAs": ["https://www.taxisalzburg.net"],
      }) }} />
    </Layout>
  );
};

export default Index;
